import { ApolloError } from '@apollo/client'
import { Button, Eyebrow, Flex, grey } from '@pelotoncycle/design-system'
import {
  useEligiblePerson,
  useEligiblePersonLookup,
  useEligiblePersonMagicLink,
  useEligiblePersonScrub,
} from 'data/hooks'
import {
  EligiblePersonSearchFields,
  EnrollmentEventAction,
} from 'data/types/graphql-global-types'
import { differenceInCalendarDays, format } from 'date-fns'
import DOMPurify from 'dompurify'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  AlertVariants,
  Field,
  LinkBackTo,
  LoadingScreen,
  NotFoundPage,
  Page,
  useAlerts,
} from 'ui/components'
import { CopyToClipboardButton } from 'ui/components/CopyToClipboardButton'
import { Modal } from 'ui/components/Modals'
import { QueryResultTable } from 'ui/components/QueryResultTable'
import { REPORTING_CHECK_ROUTE } from 'utils/constants/admin'

interface RouteParams {
  id: string
}

const getDuplicateAccountsStatusKey = (
  error: Error | undefined,
  count: number,
  settled: boolean,
): string => {
  if (!settled) return 'reporting_check.duplicate_account_status_loading'
  if (error) return 'reporting_check.duplicate_account_status_error'
  if (count === 0) return 'reporting_check.duplicate_account_status_no_data'
  if (count === 1) return 'reporting_check.duplicate_account_status_single'

  return 'reporting_check.duplicate_account_status_multiple'
}

function hasId<T extends { id: string | null }>(record: T): record is T & { id: string } {
  return record.id !== null
}

const formatDateRange = (start: string | null, end: string | null) => {
  if (!start) return ''

  const startFormatted = format(new Date(`${start}T00:00:00`), 'MMM d, y')
  const endFormatted = end ? format(new Date(`${end}T00:00:00`), 'MMM d, y') : 'Present'

  return `${startFormatted} - ${endFormatted}`
}

const EligiblePerson = () => {
  const { t } = useTranslation()
  const { id } = useParams<RouteParams>()
  const { search } = useLocation()
  const { addAlert } = useAlerts()
  const queryParams = new URLSearchParams(search)
  const partnerHashId = queryParams.get('hash')
  const { loading, error, data } = useEligiblePerson({ id, partnerHashId })
  const { scrubEligiblePerson, loading: scrubLoading } = useEligiblePersonScrub({
    eligiblePersonId: id,
    partnerHashId: partnerHashId || '',
  })
  const { refreshMagicLink, loading: refreshLoading } = useEligiblePersonMagicLink({
    eligiblePersonId: id,
  })
  const [
    eligiblePersonSearch,
    { loading: searchLoading, error: searchError, data: searchData },
  ] = useEligiblePersonLookup()
  const [refreshedMagicLink, setRefreshedMagicLink] = useState<string | undefined>(
    undefined,
  )
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showEnrollmentHistory, setShowEnrollmentHistory] = useState(false)
  const [showDuplicateAccounts, setShowDuplicateAccounts] = useState(false)

  const person = data?.eligiblePersonById

  useEffect(() => {
    if (!person?.emailAddress) return

    eligiblePersonSearch({
      variables: {
        searchTerm: person.emailAddress,
        searchFields: [EligiblePersonSearchFields.EMAIL],
        numOfResults: 25, // duplicates in practice are 2-3; 25 is a safe cap
      },
    }).catch(e => {
      /* eslint-disable-next-line no-console */
      console.error('Duplicate account lookup failed:', e)
    })
  }, [person?.emailAddress])

  const lookupResults = searchData?.eligiblePersonLookup ?? []

  const duplicateRecords = lookupResults.filter(hasId)
  const hasDuplicateRecords = duplicateRecords.length > 0
  const searchSettled = !searchLoading && (!!searchData || !!searchError)
  const duplicateAccountsStatus = t(
    getDuplicateAccountsStatusKey(searchError, lookupResults.length, searchSettled),
  )
  const hasMultipleAccounts = lookupResults.length > 1
  const canShowDuplicatesSection =
    searchSettled && showDuplicateAccounts && hasDuplicateRecords

  const duplicateAccountsField = (
    <Flex
      justifyContent="space-between"
      alignItems={hasMultipleAccounts ? 'center' : 'flex-start'}
    >
      <span>{duplicateAccountsStatus}</span>
      {hasMultipleAccounts && hasDuplicateRecords && (
        <Button
          type="button"
          color="dark"
          variant="outline"
          size="small"
          width="adjustable"
          data-testid="toggle-duplicate-accounts"
          onClick={() => setShowDuplicateAccounts(!showDuplicateAccounts)}
        >
          {showDuplicateAccounts
            ? t('reporting_check.hide_details')
            : t('reporting_check.show_details')}
        </Button>
      )}
      {hasMultipleAccounts && !hasDuplicateRecords && (
        <EmptyInline>{t('reporting_check.no_duplicate_accounts')}</EmptyInline>
      )}
    </Flex>
  )

  const handleRefreshButton = () => {
    refreshMagicLink()
      .then(result => {
        const magicLink =
          result.data?.eligiblePersonRefreshToken?.eligiblePerson?.magicLink
        if (magicLink) setRefreshedMagicLink(magicLink)
      })
      .catch(e => {
        let errorMessage = ''

        if (typeof e === 'string') errorMessage = e
        if (e instanceof ApolloError && typeof e.message === 'string')
          errorMessage = e.message

        addAlert({
          variant: AlertVariants.error,
          message: t('reporting_check.refresh_magic_link_failed', { errorMessage }),
          autoClose: 8000,
        })
      })
  }

  const currentMagicLink = refreshedMagicLink ?? person?.magicLink

  let magicLinkValue
  if (currentMagicLink) {
    magicLinkValue = (
      <MagicLinkContainer>
        <span>{currentMagicLink}</span>

        <CopyToClipboardButton
          value={currentMagicLink}
          label={t('reporting_check.magic_link')}
        />
      </MagicLinkContainer>
    )
  }

  const handleScrub = () => {
    scrubEligiblePerson().catch(e => {
      // TODO: Worth to add error message handler, many redundancy within other components
      let errorMessage = ''

      if (typeof e === 'string') errorMessage = e
      if (e instanceof ApolloError && typeof e.message === 'string')
        errorMessage = e.message

      addAlert({
        variant: AlertVariants.error,
        message: t('reporting_check.confirm_scrub_message_failed', { errorMessage }),
        autoClose: 8000,
      })
    })
    setShowConfirmModal(false)
  }

  const handleConfirmClick = () => {
    setShowConfirmModal(true)
  }

  let content
  if (loading) {
    content = <LoadingScreen />
  } else if (error || !person) {
    content = <NotFoundPage />
  } else {
    let eligiblePeriods

    if (person.eligibilityPeriods?.length) {
      eligiblePeriods = person.eligibilityPeriods?.map((period, i) => (
        <Field
          key={period.id}
          label={i === 0 ? t('reporting_check.eligibility_periods') : ''}
          value={formatDateRange(
            period.effectiveDate as string | null,
            period.terminationDate as string | null,
          )}
        />
      ))
    } else {
      eligiblePeriods = (
        <Field label={t('reporting_check.eligibility_periods')} value="--" />
      )
    }

    let cmsRiderLink = person.enrollment?.cmsRiderLink || ''
    if (cmsRiderLink) {
      cmsRiderLink = DOMPurify.sanitize(cmsRiderLink)
    }

    const { enrollment } = person

    const isEnrolled =
      !!enrollment &&
      !enrollment.enrollmentEvents.some(e => e.action === EnrollmentEventAction.REMOVE)

    const hasEnrollmentEvents = !!enrollment?.enrollmentEvents?.length

    const lastRenewEvent = enrollment?.enrollmentEvents
      .filter(e => e.action === EnrollmentEventAction.RENEW)
      .sort(
        (a, b) =>
          new Date(b.eventDate as string).getTime() -
          new Date(a.eventDate as string).getTime(),
      )[0]

    const lastRenewDate = lastRenewEvent
      ? new Date(lastRenewEvent.eventDate as string)
      : null

    const lastRenewValue = lastRenewDate
      ? t('reporting_check.last_renew_date', {
          date: format(lastRenewDate, 'MMM d, y'),
          days: differenceInCalendarDays(new Date(), lastRenewDate),
        })
      : t('reporting_check.last_renew_none')

    const enrollmentEvents = (
      <div>
        {isEnrolled ? (
          <Flex justifyContent="space-between" alignItems="center">
            <span>{t('partner.yes')}</span>

            {hasEnrollmentEvents ? (
              <Button
                type="button"
                color="dark"
                variant="outline"
                size="small"
                width="adjustable"
                data-testid="toggle-enrollment-history"
                onClick={() => setShowEnrollmentHistory(!showEnrollmentHistory)}
              >
                {showEnrollmentHistory
                  ? t('reporting_check.hide_details')
                  : t('reporting_check.show_details')}
              </Button>
            ) : (
              <EmptyInline>{t('reporting_check.no_enrollment_history')}</EmptyInline>
            )}
          </Flex>
        ) : (
          t('partner.no')
        )}
      </div>
    )

    content = (
      <>
        <Back to={REPORTING_CHECK_ROUTE} title={t('reporting_check.back_to_results')} />
        <Title size="small">{t('reporting_check.employee_info')}</Title>
        <Field label={t('reporting_check.first_name')} value={person.firstName} />
        <Field label={t('reporting_check.last_name')} value={person.lastName} />
        <Field label={t('email')} value={person.emailAddress} />

        {eligiblePeriods}

        <Field
          label={t('reporting_check.current_eligibility')}
          value={formatDateRange(
            person.currentEligibility?.effectiveDate as string,
            person.currentEligibility?.terminationDate as string,
          )}
        />
        <Field
          label={t('reporting_check.claimed_benefit')}
          value={person.enrollment?.accessToken ? 'Yes' : 'No'}
        />
        <Field label={t('reporting_check.has_subscription')} value={enrollmentEvents} />
        <Field label={t('reporting_check.last_renew')} value={lastRenewValue} />

        {showEnrollmentHistory && enrollment && (
          <EnrollmentHistorySection data-testid="enrollment-history-table">
            <EnrollmentHistoryScroll>
              <QueryResultTable
                data={enrollment.enrollmentEvents}
                columns={['id', 'action', 'eventDate', 'partnerName']}
                transforms={{
                  eventDate: (date: string) => format(new Date(date), 'MMM d, y, h:mm a'),
                }}
                variant="stacked"
              />
            </EnrollmentHistoryScroll>
          </EnrollmentHistorySection>
        )}

        <Field
          label={t('reporting_check.cms_rider_links')}
          value={
            cmsRiderLink ? (
              // eslint-disable-next-line react/no-danger
              <div dangerouslySetInnerHTML={{ __html: cmsRiderLink }} />
            ) : (
              '--'
            )
          }
        />
        <Field label={t('reporting_check.magic_link')} value={magicLinkValue} />
        <Field label={t('reporting_check.partner')} value={person.partner?.name} />
        <Field label={t('reporting_check.sponsor')} value={person.sponsor?.name} />
        <Field label={t('reporting_check.id')} value={person.id} />

        <Field
          label={t('reporting_check.duplicate_account_status')}
          value={duplicateAccountsField}
        />

        {canShowDuplicatesSection && (
          <DuplicateAccountsSection data-testid="duplicate-accounts-table">
            <DuplicateAccountsScroll>
              <QueryResultTable
                data={duplicateRecords}
                columns={['id', 'firstName']}
                customHeaders={{ firstName: 'fullName' }}
                transforms={{
                  firstName: (_firstName, row) =>
                    `${row?.firstName ?? ''} ${row?.lastName ?? ''}`.trim(),
                }}
                variant="stacked"
              />
            </DuplicateAccountsScroll>
          </DuplicateAccountsSection>
        )}

        <Flex
          margin="32px 0"
          gap="8px"
          flexDirection={{ mobile: 'column', tablet: 'row' }}
        >
          <Button
            onClick={handleConfirmClick}
            isDisabled={scrubLoading || person.firstName === 'SCRUBBED'}
            isLoading={scrubLoading}
            variant="solid"
            data-testid="scrub-button"
          >
            {t('reporting_check.scrub_employee_data')}
          </Button>

          <Button
            onClick={handleRefreshButton}
            isDisabled={refreshLoading}
            isLoading={refreshLoading}
            color="dark"
            variant="outline"
            data-testid="refresh-magic-link-button"
          >
            {t('reporting_check.refresh_magic_link')}
          </Button>
        </Flex>
        <Modal isOpen={showConfirmModal} handleClose={() => setShowConfirmModal(false)}>
          <ModalContent>
            <ModalTitle>{t('reporting_check.confirm_scrub_title')}</ModalTitle>
            <ModalMessage>{t('reporting_check.confirm_scrub_message')}</ModalMessage>
            <ModalActions>
              <Button
                onClick={() => setShowConfirmModal(false)}
                variant="outline"
                data-testid="cancel-scrub-button"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleScrub}
                variant="solid"
                color="primary"
                data-testid="confirm-scrub-button"
              >
                {t('reporting_check.confirm_scrub')}
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      </>
    )
  }

  return <Page>{content}</Page>
}

const Back = styled(LinkBackTo)`
  margin-bottom: 3rem;
`

const Title = styled(Eyebrow)`
  display: block;
  margin-bottom: 1rem;
`

const ModalContent = styled.div`
  text-align: center;
  padding: 2rem;
`

const ModalTitle = styled.h3`
  margin-bottom: 1rem;
  font-size: 1.5rem;
`

const ModalMessage = styled.p`
  margin-bottom: 2rem;
  color: #666;
`

const ModalActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`

const MagicLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const EmptyInline = styled.span`
  font-size: 0.875rem;
  color: ${grey[70]};
`

const EnrollmentHistorySection = styled.div`
  margin: 1.5rem 0;
`

const EnrollmentHistoryScroll = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid ${grey[40]};
  border-radius: 4px;
`

const DuplicateAccountsSection = styled.div`
  margin: 1rem 0;
`

const DuplicateAccountsScroll = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${grey[40]};
  border-radius: 4px;
`

export { EligiblePerson }
